import get_dom_element from './utils/get_dom_element';
import './utils/conversion_utils';
import resetInteractions from './utils/sl_reset_interactions.js';

// Seekbar CSS
import './css/seekbar_with_hiding.css';

/**
 * Function to add a seekbar to the player
 *
 * @param {Object} Player - The Player object.
 * @param {string} el - The id or class of the parent element where the seekbar is to be appended.
 * @param {string} color - The color of the seekbar.
 *
 * @returns {void}
 */
export function add_seekbar(Player, el, color) {
  // Error checking and initializations
  if (!Player || typeof Player !== 'object') {
    IVP.log('Invalid Player object', 'error');
    return;
  }

  if (!el || typeof el !== 'string') {
    IVP.log('Invalid el argument', 'error');
    return;
  }

  if (!color || typeof color !== 'string') {
    IVP.log('Invalid color argument', 'error');
    return;
  }

  // Other variable initializations
  let mouseSeekPercentage;
  Player.seekbarActive = false;
  Player.activeSlider = null;

  // Set up DOM elements
  const el_seekbarParent = get_dom_element(el, true);
  if (!el_seekbarParent) {
    IVP.log('Cannot find parent element for the seekbar', 'error');
    return;
  }

  // Create a div for the progress slider
  const progressSliderDiv = document.createElement('div');
  progressSliderDiv.classList.add('progress-slider');

  // Create a span for the tooltip text
  const tooltipTextSpan = document.createElement('span');
  tooltipTextSpan.classList.add('tooltip-text');
  tooltipTextSpan.textContent = '00:00'; // Add text content to the span

  // Append the tooltip text span to the progress slider div
  progressSliderDiv.appendChild(tooltipTextSpan);

  // Create a div for the progress bar
  const progressBarDiv = document.createElement('div');
  progressBarDiv.classList.add('progress-bar');

  // Create a div for the progress fill
  const progressFillDiv = document.createElement('div');
  progressFillDiv.classList.add('progress-fill');

  // Append the progress fill div to the progress bar div
  progressBarDiv.appendChild(progressFillDiv);

  // Append the progress bar div to the progress slider div
  progressSliderDiv.appendChild(progressBarDiv);

  // Finally, append the progress slider div to the parent element
  el_seekbarParent.appendChild(progressSliderDiv);

  // Store reference to seekbar
  Player.seekbarEl = el_seekbarParent.getElementsByClassName('progress-slider')[0];

  // Store reference to seekbar tooltip-text
  var tooltipEl = document.querySelector('.progress-slider .tooltip-text');

  // Change background color to passed color parameter
  document.documentElement.style.setProperty('--theme-color', color);

  // Make seekbar intractable
  var el_seekbar = document.getElementsByClassName('progress-slider')[0];
  el_seekbar.classList.add('webobject');

  // Progress bar functions
  function mouseSliderPosition(element, e) {
    var bcr = element.getBoundingClientRect();
    return {
      x: Math.min(Math.max(0, (e.clientX - bcr.left) / bcr.width), 1),
      y: Math.min(Math.max(0, (e.clientY - bcr.top) / bcr.height), 1),
    };
  }

  function activateSlider(e) {
    el_seekbar.classList.add('dragging');
    if (e.touches && e.touches.length > 1) {
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();

    Player.activeSlider = this;

    handleSliderMove(e);
  }

  function handleSliderMove(e) {
    el_seekbar.classList.add('dragging');
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.touches && e.touches.length > 1) {
      return;
    }
    if (Player.activeSlider) {
      var value = mouseSliderPosition(progressBarDiv, (e.touches && e.touches[0]) || e).x;
      progressFillDiv.style.transform = 'scaleX(' + value + ')';
      mouseSeekPercentage = value;
      tooltipEl.style.left = value * 100 + '%';
      var mouseDragTime = Player.get_state().endTime * value;
      tooltipEl.innerHTML = mouseDragTime.toHHMMSS();
    }
  }

  function deactivateSlider(e) {
    if (e.touches && e.touches.length > 0) {
      return;
    }
    if (Player.activeSlider) {
      Player.activeSlider = null;
      var vidTime = Player.get_state().endTime * mouseSeekPercentage;

      resetInteractions();
      Player.seek_to(vidTime);
      Player.play();
    }
  }

  // Seekbar listeners
  let progress_slider = document.querySelector('.progress-slider');
  progress_slider.addEventListener('mousedown', activateSlider);
  progress_slider.addEventListener('touchstart', activateSlider);
  progress_slider.addEventListener('mousemove', handleSliderMove);
  progress_slider.addEventListener('mouseup', deactivateSlider);
  progress_slider.addEventListener('touchmove', handleSliderMove);
  progress_slider.addEventListener('touchend', deactivateSlider);

  Player.seekbarActive = true;

  // run function after 1 second to allow seekbar to be visible
  setTimeout(function () {
    update_seekbar_visibility(Player, false);
  }, 2000);
}

/**
 * Function to update seekbar progress
 *
 * @param {Object} Player - The Player object.
 * @param {number} seconds - The current time of the video in seconds.
 *
 * @returns {void}
 */
export function update_seekbar(Player, seconds) {
  // Error checking
  if (!Player || typeof Player !== 'object') {
    IVP.log('Invalid Player object', 'error');
    return;
  }

  if (typeof seconds !== 'number') {
    IVP.log('Invalid seconds argument', 'error');
    return;
  }

  if (SL.get('vtSeekbarEnabled') && Player.seekbarActive) {
    // Has seekbar been enabled and is it active?
    if (Player.activeSlider == null) {
      var percentage = seconds / Player.get_state().endTime;

      const progressFill = document.getElementsByClassName('progress-fill')[0];
      const tooltipEl = document.getElementsByClassName('tooltip-text')[0];
      progressFill.style.transform = 'scaleX(' + percentage + ')';
      tooltipEl.innerHTML = seconds.toHHMMSS();
      tooltipEl.style.left = percentage * 100 + '%';
    }
  }
}

/**
 * Function to update visibility of the seekbar
 *
 * @param {Object} Player - The Player object.
 * @param {boolean} state - The visibility state of the seekbar. If true, seekbar is shown, otherwise hidden.
 *
 * @returns {void}
 */
export function update_seekbar_visibility(Player, state) {
  // Error checking
  if (!Player || typeof Player !== 'object') {
    IVP.log('Invalid Player object', 'error');
    return;
  }

  if (typeof state !== 'boolean') {
    IVP.log('Invalid state argument', 'error');
    return;
  }

  if (SL.get('vtSeekbarEnabled') && Player.seekbarActive) {
    // Has seekbar been enabled and is it active?
    let progress_slider = document.querySelector('.progress-slider');
    if (progress_slider && state) {
      // True - show seekbar
      progress_slider.classList.remove('paused');
    } else if (progress_slider) {
      // False - hide seekbar
      progress_slider.classList.add('paused');
    }
  }
}

/**
 * Function to remove the seekbar
 *
 * @param {Object} Player - The Player object.
 *
 * @returns {void}
 */
export function remove_seekbar(Player) {
  // Error checking
  if (!Player || typeof Player !== 'object') {
    IVP.log('Invalid Player object', 'error');
    return;
  }

  if (Player.seekbarEl) {
    IVP.log(`[Removing seekbar]`, 'action');
    if (Player.player) {
      Player.seekbarActive = false;
    }
    Player.seekbarEl.remove();
  }
}
